import { parseDateFilterParam } from "@/components/date-filter/date-filter.type.ts";
import { IS_MANAGER_COLUMN } from "@/pages/employees/index/use-employee-column-filters.ts";
import {
  ApiEmployeeColumnFilter,
  ApiEmployeeColumnFilters,
} from "@/types/employees/employee-column.types.ts";

export const LEGACY_EMPLOYEE_FILTER_KEYS = [
  "manager_ids",
  "division_ids",
  "department_ids",
  "job_profile_ids",
  "job_role_ids",
  "employee_lifecycle_statuses",
  "employment_type_ids",
  "employment_contract_ids",
  "location_ids",
  "hired_on",
  "birthday",
  "termination_date",
] as const;

const COLUMN_FILTER_KEYS: (keyof ApiEmployeeColumnFilters)[] = [
  "personal_filter",
  "position_filter",
  "employment_filter",
  "compensation_filter",
  "termination_filter",
  "additional_compensation_filter",
  "custom_fields_filter",
  "custom_table_filter",
];

type LegacyFilterKey = (typeof LEGACY_EMPLOYEE_FILTER_KEYS)[number];
type FilterValueKey = "option_ids" | "employee_ids" | "date_filter";

const LEGACY_FILTER_MAPPING: Record<LegacyFilterKey, { field: string; valueKey: FilterValueKey }> =
  {
    manager_ids: { field: "employee.position.manager", valueKey: "employee_ids" },
    division_ids: { field: "employee.position.division", valueKey: "option_ids" },
    department_ids: { field: "employee.position.department", valueKey: "option_ids" },
    job_profile_ids: { field: "employee.position.job_profile", valueKey: "option_ids" },
    job_role_ids: { field: "employee.position.job_role", valueKey: "option_ids" },
    employee_lifecycle_statuses: { field: "employee.lifecycle_status", valueKey: "option_ids" },
    employment_type_ids: { field: "employee.employment.type", valueKey: "option_ids" },
    employment_contract_ids: { field: "employee.employment.contract", valueKey: "option_ids" },
    location_ids: { field: "employee.position.location", valueKey: "option_ids" },
    hired_on: { field: "employee.hired_on", valueKey: "date_filter" },
    birthday: { field: "employee.birth_date", valueKey: "date_filter" },
    termination_date: { field: "employee.termination.date", valueKey: "date_filter" },
  };

const parseArray = (value: unknown): (number | string)[] => {
  const items = Array.isArray(value) ? value : typeof value === "string" ? value.split(",") : [];
  return items
    .map((item) => {
      if (typeof item === "number") return item;
      if (typeof item !== "string") return undefined;
      const normalized = item.trim();
      if (!normalized) return undefined;
      const numberValue = Number(normalized);
      return Number.isFinite(numberValue) ? numberValue : normalized;
    })
    .filter((item): item is number | string => item !== undefined);
};

const flattenColumnFilters = (value: Record<string, unknown>): ApiEmployeeColumnFilter[] =>
  COLUMN_FILTER_KEYS.flatMap((key) => {
    const filters = value[key];
    return Array.isArray(filters)
      ? filters.filter(
          (filter): filter is ApiEmployeeColumnFilter =>
            Boolean(filter) && typeof filter === "object" && typeof filter.field === "string"
        )
      : [];
  });

export const migrateLegacyEmployeeFilters = (
  value: Record<string, unknown>,
  availableFields: ReadonlySet<string>,
  existingFilters: ApiEmployeeColumnFilter[] = []
): ApiEmployeeColumnFilter[] => {
  const result = [...existingFilters, ...flattenColumnFilters(value)];
  const mappedFields = new Set(result.map((filter) => filter.field));

  for (const legacyKey of LEGACY_EMPLOYEE_FILTER_KEYS) {
    const mapping = LEGACY_FILTER_MAPPING[legacyKey];
    if (!availableFields.has(mapping.field) || mappedFields.has(mapping.field)) continue;

    const rawValue = value[legacyKey];
    if (mapping.valueKey === "date_filter") {
      const dateFilter = parseDateFilterParam(rawValue);
      if (!dateFilter) continue;
      result.push({ field: mapping.field, date_filter: dateFilter });
      mappedFields.add(mapping.field);
      continue;
    }

    const ids = parseArray(rawValue);
    // «Все статусы»: в URL пустая строка, в сохранённой вьюхе — пустой массив
    if (
      legacyKey === "employee_lifecycle_statuses" &&
      (rawValue === "" || (Array.isArray(rawValue) && rawValue.length === 0))
    ) {
      result.push({ field: mapping.field, option_ids: [] });
      mappedFields.add(mapping.field);
      continue;
    }
    if (!ids.length) continue;
    result.push({ field: mapping.field, [mapping.valueKey]: ids });
    mappedFields.add(mapping.field);
  }

  if (
    typeof value.is_manager === "boolean" &&
    availableFields.has(IS_MANAGER_COLUMN) &&
    !mappedFields.has(IS_MANAGER_COLUMN)
  ) {
    result.push({ field: IS_MANAGER_COLUMN, switch: value.is_manager });
    mappedFields.add(IS_MANAGER_COLUMN);
  }

  return result;
};

export const migrateLegacyEmployeeFilterParams = (
  source: URLSearchParams,
  availableFields: ReadonlySet<string>,
  columnFiltersParam: string
): { params: URLSearchParams; changed: boolean } => {
  const params = new URLSearchParams(source);
  const legacyValue = Object.fromEntries(
    LEGACY_EMPLOYEE_FILTER_KEYS.flatMap((key) => {
      const value = params.get(key);
      return value === null ? [] : [[key, value]];
    })
  );
  const hasLegacyFilters = Object.keys(legacyValue).length > 0;
  if (!hasLegacyFilters) return { params, changed: false };

  let existingFilters: ApiEmployeeColumnFilter[] = [];
  const rawColumnFilters = params.get(columnFiltersParam);
  if (rawColumnFilters) {
    try {
      const parsed = JSON.parse(rawColumnFilters);
      if (Array.isArray(parsed)) existingFilters = parsed;
    } catch {
      existingFilters = [];
    }
  }

  const migrated = migrateLegacyEmployeeFilters(legacyValue, availableFields, existingFilters);
  if (migrated.length) params.set(columnFiltersParam, JSON.stringify(migrated));
  LEGACY_EMPLOYEE_FILTER_KEYS.forEach((key) => params.delete(key));
  params.delete("page");

  return { params, changed: true };
};
